/**
 * NetworkStylePanel - Panel de edición de estilos de Red Urbana
 *
 * @module core/spatial/components
 * @version 1.0
 * @created 2026-03-02
 *
 * @description
 * Panel de edición de estilos visuales para la Red Vial Canónica.
 * Se muestra en el RightPanel y permite al usuario personalizar:
 * - Colores de edges y nodes
 * - Anchos y radios
 * - Opacidades
 * - Aplicar presets predefinidos
 *
 * Los cambios se guardan automáticamente en localStorage por proyecto.
 *
 * @example
 * ```typescript
 * // En RightPanel
 * {activePanel === 'network-style' && <NetworkStylePanel />}
 * ```
 */

import { RotateCcw } from 'lucide-react';
import { useNetworkStyle } from '../contexts/NetworkStyleContext';
import { NETWORK_STYLE_PRESETS } from '../config/network-style.config';
import './network-style-panel.css';

/**
 * Panel de edición de estilos de red
 *
 * @returns Componente del panel
 */
export function NetworkStylePanel(): JSX.Element {
  const { style, updateStyle, applyPreset, resetToDefault } = useNetworkStyle();

  return (
    <div className="network-style-panel">
      {/* Header */}
      <div className="nsp-header">
        <h2 className="nsp-title">Estilos de Red Urbana</h2>
        <button
          className="nsp-reset-btn"
          onClick={resetToDefault}
          title="Resetear a valores por defecto"
          aria-label="Resetear estilos"
        >
          <RotateCcw size={14} />
        </button>
      </div>

      {/* Presets Section */}
      <section className="nsp-section">
        <h3 className="nsp-section-title">Presets</h3>
        <div className="nsp-presets">
          {Object.keys(NETWORK_STYLE_PRESETS).map((presetName) => (
            <button
              key={presetName}
              className="nsp-preset-btn"
              onClick={() => applyPreset(presetName as keyof typeof NETWORK_STYLE_PRESETS)}
            >
              {presetName === 'default' && 'Por Defecto'}
              {presetName === 'highContrast' && 'Alto Contraste'}
              {presetName === 'subtle' && 'Sutil'}
              {presetName === 'emphasis' && 'Énfasis'}
            </button>
          ))}
        </div>
      </section>

      {/* Edges Section */}
      <section className="nsp-section">
        <h3 className="nsp-section-title">Segmentos (Edges)</h3>

        {/* Color */}
        <div className="nsp-control">
          <label className="nsp-label" htmlFor="edge-color">
            Color
          </label>
          <div className="nsp-color-input">
            <input
              id="edge-color"
              type="color"
              value={style.edges.color}
              onChange={(e) =>
                updateStyle({
                  edges: { ...style.edges, color: e.target.value },
                })
              }
              className="nsp-color-picker"
            />
            <span className="nsp-color-value">{style.edges.color}</span>
          </div>
        </div>

        {/* Width */}
        <div className="nsp-control">
          <label className="nsp-label" htmlFor="edge-width">
            Ancho: {style.edges.width}px
          </label>
          <input
            id="edge-width"
            type="range"
            min="1"
            max="10"
            step="0.5"
            value={style.edges.width}
            onChange={(e) =>
              updateStyle({
                edges: { ...style.edges, width: parseFloat(e.target.value) },
              })
            }
            className="nsp-slider"
          />
        </div>

        {/* Opacity */}
        <div className="nsp-control">
          <label className="nsp-label" htmlFor="edge-opacity">
            Opacidad: {Math.round(style.edges.opacity * 100)}%
          </label>
          <input
            id="edge-opacity"
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={style.edges.opacity}
            onChange={(e) =>
              updateStyle({
                edges: { ...style.edges, opacity: parseFloat(e.target.value) },
              })
            }
            className="nsp-slider"
          />
        </div>
      </section>

      {/* Nodes Section */}
      <section className="nsp-section">
        <h3 className="nsp-section-title">Nodos (Intersecciones)</h3>

        {/* Fill Color */}
        <div className="nsp-control">
          <label className="nsp-label" htmlFor="node-fill-color">
            Color de Relleno
          </label>
          <div className="nsp-color-input">
            <input
              id="node-fill-color"
              type="color"
              value={style.nodes.fillColor}
              onChange={(e) =>
                updateStyle({
                  nodes: { ...style.nodes, fillColor: e.target.value },
                })
              }
              className="nsp-color-picker"
            />
            <span className="nsp-color-value">{style.nodes.fillColor}</span>
          </div>
        </div>

        {/* Stroke Color */}
        <div className="nsp-control">
          <label className="nsp-label" htmlFor="node-stroke-color">
            Color de Borde
          </label>
          <div className="nsp-color-input">
            <input
              id="node-stroke-color"
              type="color"
              value={style.nodes.strokeColor}
              onChange={(e) =>
                updateStyle({
                  nodes: { ...style.nodes, strokeColor: e.target.value },
                })
              }
              className="nsp-color-picker"
            />
            <span className="nsp-color-value">{style.nodes.strokeColor}</span>
          </div>
        </div>

        {/* Radius */}
        <div className="nsp-control">
          <label className="nsp-label" htmlFor="node-radius">
            Radio: {style.nodes.radius}px
          </label>
          <input
            id="node-radius"
            type="range"
            min="2"
            max="20"
            step="1"
            value={style.nodes.radius}
            onChange={(e) =>
              updateStyle({
                nodes: { ...style.nodes, radius: parseFloat(e.target.value) },
              })
            }
            className="nsp-slider"
          />
        </div>

        {/* Stroke Width */}
        <div className="nsp-control">
          <label className="nsp-label" htmlFor="node-stroke-width">
            Ancho de Borde: {style.nodes.strokeWidth}px
          </label>
          <input
            id="node-stroke-width"
            type="range"
            min="0"
            max="5"
            step="0.5"
            value={style.nodes.strokeWidth}
            onChange={(e) =>
              updateStyle({
                nodes: { ...style.nodes, strokeWidth: parseFloat(e.target.value) },
              })
            }
            className="nsp-slider"
          />
        </div>

        {/* Opacity */}
        <div className="nsp-control">
          <label className="nsp-label" htmlFor="node-opacity">
            Opacidad: {Math.round(style.nodes.opacity * 100)}%
          </label>
          <input
            id="node-opacity"
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={style.nodes.opacity}
            onChange={(e) =>
              updateStyle({
                nodes: { ...style.nodes, opacity: parseFloat(e.target.value) },
              })
            }
            className="nsp-slider"
          />
        </div>
      </section>

      {/* Info */}
      <div className="nsp-info">
        <span className="nsp-info-icon">ℹ</span>
        <span className="nsp-info-text">
          Los cambios se guardan automáticamente por proyecto
        </span>
      </div>
    </div>
  );
}
